import { useCallback, useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";

import collaborator1 from "../../../assets/feature-tour/collaborator-1.webp";
import collaborator2 from "../../../assets/feature-tour/collaborator-2.webp";
import member1 from "../../../assets/feature-tour/member-1.webp";
import member2 from "../../../assets/feature-tour/member-2.webp";
import { useGetAppProperties, useSetAppProperties } from "../../../hooks/useGetAppProperties";
import { useProject } from "../../../hooks/useProject";
import { useTracker } from "../../../hooks/useTracker";
import { useDeepLinkStateStore } from "../../../stores/deepLinkStateStore";
import { SearchParam } from "../../../types";
import { FeatureTour, type FeatureTourSlideItem } from "../FeatureTour/FeatureTour";

const slidesCollaborator: FeatureTourSlideItem[] = [
  {
    img: collaborator1,
    title: "You're now a collaborator",
    description:
      "View the model and add comments in browser. For full collaboration, open in Shapr3D to start editing together.",
  },
  {
    img: collaborator2,
    title: "Ready to edit?",
    description:
      "Download Shapr3D to access full modeling capabilities. Your edits will sync with everyone working on this project.",
  },
];

const slidesMember: FeatureTourSlideItem[] = [
  {
    img: member1,
    title: "Share a single project via web link",
    description:
      "Project sharing is now this simple: click a link, get access and launch Shapr3D to start editing.",
  },
  {
    img: member2,
    title: "Two ways to contribute",
    description:
      "For collaboration, open the app to access full modeling capabilities. When versions are published, use them to add feedback directly on the model.",
  },
];

export const ProjectPageFeatureTour = () => {
  const { data: projectData } = useProject();

  const tracker = useTracker();

  const trackSlideDisplay = useCallback(
    (slideIndex: number, featureTourType: string) => {
      tracker.trackProjectPageFeatureTourStepShown({
        featureTourStep: slideIndex,
        featureTourType: featureTourType,
        projectId: projectData?.project.projectId || "",
      });
    },
    [tracker, projectData?.project.projectId]
  );

  const trackFeatureTourCompleted = useCallback(
    (featureTourType: string) => {
      tracker.trackProjectPageFeatureTourCompleted({
        featureTourType: featureTourType,
        projectId: projectData?.project.projectId || "",
      });
    },
    [tracker, projectData?.project.projectId]
  );

  const trackFeatureTourClose = useCallback(
    (featureTourType: string) => {
      tracker.trackProjectPageFeatureTourClose({
        featureTourType: featureTourType,
        projectId: projectData?.project.projectId || "",
      });
    },
    [tracker, projectData?.project.projectId]
  );

  const [searchParams] = useSearchParams();
  const { setIsModalOpen: setIsDeepLinkModalOpen } = useDeepLinkStateStore();

  // Avoid showing the tour if there are intent params in the url (e.g. ?intent=share or ?intent=invite)
  const hasIntentParams = searchParams.get(SearchParam.Intent);
  const [isFeatureTourOpen, setIsFeatureTourOpen] = useState(!hasIntentParams);

  const { data: appPropertiesData, isSuccess: appPropertiesIsSuccess } = useGetAppProperties();
  const { mutateAsync: mutateAppProperties } = useSetAppProperties();

  const showFeatureTourMember = useMemo(() => {
    return (
      projectData?.userRole &&
      projectData.userRole === "owner" &&
      appPropertiesIsSuccess &&
      !appPropertiesData?.web_feature_tour_project_sharing_owner_completed
    );
  }, [projectData?.userRole, appPropertiesData, appPropertiesIsSuccess]);

  const showFeatureTourCollaborator = useMemo(() => {
    return (
      projectData?.userRole &&
      projectData.userRole === "editor" &&
      appPropertiesIsSuccess &&
      !appPropertiesData?.web_feature_tour_project_sharing_collaborator_completed
    );
  }, [projectData?.userRole, appPropertiesData, appPropertiesIsSuccess]);

  if (showFeatureTourCollaborator)
    return (
      <FeatureTour
        slides={slidesCollaborator.map((slide, index) => ({
          ...slide,
          onDisplay: () => trackSlideDisplay(index, "team-collaborator"),
        }))}
        isOpen={isFeatureTourOpen}
        onClose={() => {
          mutateAppProperties({
            web_feature_tour_project_sharing_collaborator_completed: true,
          });
          setIsFeatureTourOpen(false);
          trackFeatureTourClose("team-collaborator");
        }}
        onComplete={() => {
          setIsFeatureTourOpen(false);
          setIsDeepLinkModalOpen(true);
          trackFeatureTourCompleted("team-collaborator");
        }}
        completeLabel="Open in Shapr3D"
        telemetryName="project_page_feature_tour"
      />
    );

  if (showFeatureTourMember)
    return (
      <FeatureTour
        slides={slidesMember.map((slide, index) => ({
          ...slide,
          onDisplay: () => trackSlideDisplay(index, "team-member"),
        }))}
        isOpen={isFeatureTourOpen}
        onClose={() => {
          mutateAppProperties({
            web_feature_tour_project_sharing_owner_completed: true,
          });
          setIsFeatureTourOpen(false);
          trackFeatureTourClose("team-member");
        }}
        onComplete={() => {
          setIsFeatureTourOpen(false);
          trackFeatureTourCompleted("team-member");
        }}
        completeLabel="Go to Project"
        telemetryName="project_page_feature_tour"
      />
    );

  return null;
};
